import React, { useEffect, useState } from 'react';
import {
  Table,
  App,
  Button,
  Space,
  Input,
  DatePicker,
  Tag,
  Tooltip,
  Select
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  FileAddOutlined,
  SendOutlined,
  PlusOutlined,
  BankOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { StyledCard } from '../common';
import { theme } from '../../theme';
import GrabarAsientoModal from './GrabarAsientoModal';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface ServicioDesmote {
  id: number;
  numero_servicio: number;
  comprobante: string;
  fecha: string;
  cliente: number;
  cliente_nombre: string;
  cliente_nc: string;
  subtotal: number;
  iva: number;
  impuesto_interno: number;
  retenciones: number;
  total: number;
  total_kilos: number;
  alta: number;
  empresa: number;
  empresa_nombre: string;
  sucursal: number;
  sucursal_nombre: string;
  asiento_contable_id: number | null;
  tipo_comprobante_contable_id: number | null;
}

interface Cliente {
  id: number;
  denominacion: string;
  nc: string;
}

const ListarServicioDesmote: React.FC = () => {
  const { message, modal } = App.useApp();
  const [servicios, setServicios] = useState<ServicioDesmote[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const [tieneContable, setTieneContable] = useState(false);
  const [modalAsiento, setModalAsiento] = useState<{ open: boolean; servicio: ServicioDesmote | null }>({ open: false, servicio: null });

  const [searchCliente, setSearchCliente] = useState('');
  const [showSearchCliente, setShowSearchCliente] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);

  const [filtros, setFiltros] = useState({
    fechaDesde: dayjs().subtract(30, 'day') as dayjs.Dayjs | null,
    fechaHasta: dayjs() as dayjs.Dayjs | null,
    cliente: undefined as number | undefined,
    estado: '' as string,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (user?.id_empresa) {
      fetchClientes();
      fetchServicios();
      getAccessTokenSilently().then(token => {
        axios.get(
          `${process.env.REACT_APP_LIQUIDACION_URL}/verificar_empresa_contable/${user.id_empresa}/`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).then(res => setTieneContable(res.data.tiene_contable === true)).catch(() => {});
      }).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (user?.id_empresa && (currentPage > 1 || pageSize !== 10)) {
      fetchServicios();
    }
  }, [user, currentPage, pageSize]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.cliente-search-container')) {
        setShowSearchCliente(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchClientes = async () => {
    if (!user?.id_empresa) return;
    setLoadingClientes(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get<Cliente[]>(
        `${process.env.REACT_APP_PADRON_URL}/leer_padron/${user.id_empresa}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClientes(response.data);
    } catch {
      message.error('Error al cargar la lista de clientes');
    } finally {
      setLoadingClientes(false);
    }
  };

  const fetchServicios = async () => {
    if (!user?.id_empresa) return;
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      let url = `${process.env.REACT_APP_LIQUIDACION_URL}/listar_servicios_desmote/${user.id_empresa}/`;
      if (filtros.cliente) url += `${filtros.cliente}/`;

      const params: any = {
        id_empresa: user.id_empresa,
        id_sucursal: user.id_sucursal,
        page: currentPage,
        page_size: pageSize,
      };
      if (filtros.fechaDesde) params.fecha_desde = filtros.fechaDesde.format('YYYY-MM-DD');
      if (filtros.fechaHasta) params.fecha_hasta = filtros.fechaHasta.format('YYYY-MM-DD');
      if (filtros.estado) params.estado = filtros.estado;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      if (response.data.results) {
        setServicios(response.data.results);
        setTotal(response.data.count || 0);
      } else {
        setServicios(response.data);
        setTotal(response.data.length);
      }
      setHasSearched(true);
    } catch {
      message.error('Error al cargar los servicios de desmote');
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = () => {
    setCurrentPage(1);
    setHasSearched(true);
    fetchServicios();
  };

  const handleLimpiarFiltros = () => {
    setFiltros({
      fechaDesde: dayjs().subtract(30, 'day'),
      fechaHasta: dayjs(),
      cliente: undefined,
      estado: '',
    });
    setSelectedCliente(null);
    setSearchCliente('');
    setShowSearchCliente(false);
    setCurrentPage(1);
    setHasSearched(false);
    setServicios([]);
    setTotal(0);
  };

  const handleFechaChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    setFiltros(prev => ({
      ...prev,
      fechaDesde: dates?.[0] ?? null,
      fechaHasta: dates?.[1] ?? null,
    }));
  };

  const handleClienteSelect = (cliente: Cliente) => {
    setSelectedCliente(cliente);
    setFiltros(prev => ({ ...prev, cliente: cliente.id }));
    setShowSearchCliente(false);
  };

  const handleClienteClear = () => {
    setSelectedCliente(null);
    setSearchCliente('');
    setShowSearchCliente(true);
    setFiltros(prev => ({ ...prev, cliente: undefined }));
  };

  const handleNuevoServicio = () => {
    navigate('/liquidacion_servicio');
  };

  const handleAsientoSuccess = (numeroAsiento: number, nroComprobante: string) => {
    const idGrabado = modalAsiento.servicio?.id;
    setModalAsiento({ open: false, servicio: null });
    message.success(`Asiento N° ${numeroAsiento} grabado — Cpte: ${nroComprobante}`);
    setServicios(prev => prev.map(s =>
      s.id === idGrabado
        ? { ...s, asiento_contable_id: numeroAsiento, comprobante: nroComprobante }
        : s
    ));
  };

  const getEstadoTag = (alta: number) => {
    switch (alta) {
      case 1: return <Tag color="green">Activo</Tag>;
      case 0: return <Tag color="red">Inactivo</Tag>;
      case 2: return <Tag color="blue">Emitido</Tag>;
      default: return <Tag color="default">Desconocido</Tag>;
    }
  };

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) return '$ 0';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-AR').format(numValue);
  };

  const columns: ColumnsType<ServicioDesmote> = [
    {
      title: 'N° Servicio',
      dataIndex: 'numero_servicio',
      key: 'numero_servicio',
      width: 100,
      sorter: (a, b) => a.numero_servicio - b.numero_servicio,
    },
    {
      title: 'Comprobante',
      dataIndex: 'comprobante',
      key: 'comprobante',
      width: 150,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 100,
      render: (value) => dayjs(value).format('DD/MM/YYYY'),
      sorter: (a, b) => dayjs(a.fecha).unix() - dayjs(b.fecha).unix(),
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente_nombre',
      key: 'cliente_nombre',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'N° Cliente',
      dataIndex: 'cliente_nc',
      key: 'cliente_nc',
      width: 100,
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      width: 120,
      align: 'right',
      render: (value) => `$ ${formatCurrency(value)}`,
    },
    {
      title: 'IVA',
      dataIndex: 'iva',
      key: 'iva',
      width: 100,
      align: 'right',
      render: (value) => `$ ${formatCurrency(value)}`,
    },
    {
      title: 'Percepciones',
      dataIndex: 'retenciones',
      key: 'retenciones',
      width: 120,
      align: 'right',
      render: (value) => `$ ${formatCurrency(value)}`,
    },
    {
      title: 'Total Kilos',
      dataIndex: 'total_kilos',
      key: 'total_kilos',
      width: 110,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Estado',
      dataIndex: 'alta',
      key: 'alta',
      width: 80,
      align: 'center',
      render: (value) => getEstadoTag(value),
      filters: [
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 },
        { text: 'Emitido', value: 2 },
      ],
      onFilter: (value, record) => record.alta === value,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 200,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          {user?.tipo_usuario === 'D' && record.alta !== 0 && (
            <Tooltip title={record.asiento_contable_id != null ? 'No editable: asiento registrado' : 'Editar'}>
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                disabled={record.asiento_contable_id != null}
                onClick={() => navigate(`/editar_servicio_desmote/${record.id}`)}
              />
            </Tooltip>
          )}
          <Tooltip title="Imprimir">
            <Button
              icon={<PrinterOutlined />}
              size="small"
              onClick={() => navigate(`/imprimir_servicio_desmote/${record.id}`)}
            />
          </Tooltip>
          {(() => {
            const cpte = record.comprobante || '';
            const sinAfip = cpte.startsWith('0000');
            const asientoHecho = record.asiento_contable_id != null;
            const asientoHabilitado = !asientoHecho && !sinAfip && record.alta !== 0;

            return (
              <>
                <Tooltip title="Emitir">
                  <Button
                    icon={<SendOutlined />}
                    size="small"
                    style={{ backgroundColor: theme.colors.secondary, borderColor: theme.colors.secondary, color: 'white' }}
                    onClick={() => navigate(`/emision_serviciodesmote/${record.id}`)}
                  />
                </Tooltip>
                {tieneContable && (
                  <Tooltip title={
                    record.alta === 0 ? 'No disponible: liquidación anulada' :
                    asientoHabilitado ? 'Grabar Asiento Contable' :
                    `Asiento N° ${record.asiento_contable_id} — Cpte: ${cpte}`
                  }>
                    <Button
                      icon={<BankOutlined />}
                      size="small"
                      style={
                        asientoHabilitado
                          ? { backgroundColor: '#6366f1', borderColor: '#6366f1', color: 'white' }
                          : { backgroundColor: theme.colors.success, borderColor: theme.colors.success, color: 'white' }
                      }
                      disabled={!asientoHabilitado}
                      onClick={asientoHabilitado ? () => setModalAsiento({ open: true, servicio: record }) : undefined}
                    />
                  </Tooltip>
                )}
              </>
            );
          })()}
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  return (
    <div style={{ padding: '0' }}>
      {modalAsiento.servicio && (
        <GrabarAsientoModal
          open={modalAsiento.open}
          onClose={() => setModalAsiento({ open: false, servicio: null })}
          onSuccess={handleAsientoSuccess}
          tipo="servicio"
          idEmpresa={modalAsiento.servicio.empresa}
          idSucursal={modalAsiento.servicio.sucursal}
          idLiquidacion={modalAsiento.servicio.id}
          nroInterno={modalAsiento.servicio.numero_servicio}
          clienteNombre={modalAsiento.servicio.cliente_nombre}
          subtotal={modalAsiento.servicio.subtotal}
          iva={modalAsiento.servicio.iva}
          retenciones={modalAsiento.servicio.retenciones}
        />
      )}

      <h1 style={{
        color: theme.colors.primaryDark,
        fontSize: '28px',
        fontWeight: 600,
        marginBottom: '24px',
      }}>
        Liquidación Servicios Desmote
      </h1>

      <StyledCard
        title="Filtros de Búsqueda"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleNuevoServicio}
            size="large"
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              minWidth: '160px',
            }}
          >
            Nuevo Servicio
          </Button>
        }
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '20px',
          marginBottom: '24px',
          alignItems: 'end',
        }}>
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '14px',
            }}>
              Fecha (Desde - Hasta):
            </label>
            <RangePicker
              size="large"
              style={{ width: '100%' }}
              value={[filtros.fechaDesde, filtros.fechaHasta]}
              onChange={handleFechaChange}
              format="DD/MM/YYYY"
              placeholder={['Desde', 'Hasta']}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '14px',
            }}>
              Cliente:
            </label>
            <div style={{ position: 'relative' }} className="cliente-search-container">
              <Input
                placeholder="Buscar cliente..."
                size="large"
                prefix={<SearchOutlined />}
                value={selectedCliente ? `${selectedCliente.nc} - ${selectedCliente.denominacion}` : searchCliente}
                onChange={e => {
                  setSearchCliente(e.target.value);
                  setSelectedCliente(null);
                  setShowSearchCliente(true);
                }}
                onFocus={() => setShowSearchCliente(true)}
                allowClear={!!selectedCliente}
                onClear={handleClienteClear}
                style={{ backgroundColor: selectedCliente ? theme.colors.backgroundGray : 'white' }}
              />
              {showSearchCliente && clientes.length > 0 && !selectedCliente && (
                <div style={{
                  position: 'absolute',
                  top: '100%', left: 0, right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}>
                  {clientes
                    .filter(c => {
                      const s = searchCliente.trim().toLowerCase();
                      return c.nc.toLowerCase().includes(s) || c.denominacion.toLowerCase().includes(s);
                    })
                    .map(cliente => (
                      <div
                        key={cliente.id}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                        onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                        onClick={() => handleClienteSelect(cliente)}
                      >
                        {cliente.nc} - {cliente.denominacion}
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '14px'
            }}>
              Estado:
            </label>
            <Select
              value={filtros.estado || undefined}
              placeholder="Todos"
              allowClear
              size="large"
              style={{ width: '100%' }}
              onChange={(value) => setFiltros({ ...filtros, estado: value ?? '' })}
            >
              <Option value="pendiente_asiento">Pendiente de asiento</Option>
              <Option value="afip_pendiente_asiento">Con AFIP, pendiente de asiento</Option>
              <Option value="asentado">Asentado en diario</Option>
              <Option value="anulado">Anulado</Option>
            </Select>
          </div>

          <div>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleBuscar}
              loading={loading}
              size="large"
              style={{
                backgroundColor: theme.colors.secondary,
                borderColor: theme.colors.secondary,
                fontWeight: 500,
                width: '100%',
              }}
            >
              Buscar
            </Button>
          </div>
          <div>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleLimpiarFiltros}
              size="large"
              style={{ fontWeight: 500, width: '100%' }}
            >
              Limpiar
            </Button>
          </div>
        </div>

        {!hasSearched ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: theme.colors.textMuted,
            backgroundColor: theme.colors.backgroundGray,
            borderRadius: '6px',
            border: `1px dashed ${theme.colors.border}`,
          }}>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>
              Configure los filtros y haga clic en "Buscar" para ver los servicios de desmote
            </div>
            <div style={{ fontSize: '14px' }}>
              Las fechas están predeterminadas para los últimos 30 días
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={servicios}
            rowKey="id"
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (tot, range) => `${range[0]}-${range[1]} de ${tot} servicios`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            scroll={{ x: 1300 }}
            size="middle"
            locale={{ emptyText: 'No se encontraron servicios con los filtros seleccionados' }}
          />
        )}
      </StyledCard>
    </div>
  );
};

export default ListarServicioDesmote;
